import { useId, useState, type FormEvent } from "react";
import { Plus, X } from "lucide-react";
import type { JournalInput, JournalLineInput, LedgerAccount } from "@/api/books";
import { RequestError } from "@/components/RequestError";
import { Button } from "@/components/ui/button";
import { SelectField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { todayIso } from "@/lib/dates";
import { formatInr } from "@/lib/format";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type JournalFormProps = {
  accounts: LedgerAccount[];
  onSubmit: (input: JournalInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

type LineDraft = { account_id: string; debit: string; credit: string; party_label: string; memo: string };

const EMPTY_LINE: LineDraft = { account_id: "", debit: "", credit: "", party_label: "", memo: "" };
const MIN_LINES = 2;

function sum(lines: LineDraft[], side: "debit" | "credit"): number {
  return lines.reduce((total, line) => total + (Number(line[side]) || 0), 0);
}

function toInput(line: LineDraft): JournalLineInput {
  return {
    account_id: line.account_id,
    debit_inr: line.debit.trim() || "0.00",
    credit_inr: line.credit.trim() || "0.00",
    party_kind: "",
    party_id: null,
    party_label: line.party_label.trim(),
    fx_currency: "",
    fx_amount: null,
    fx_rate: null,
    memo: line.memo.trim(),
  };
}

/** A manual entry: the CA's adjustments, opening balances, a provision. The entry is sent
 *  only when its debits equal its credits. */
export function JournalForm({ accounts, onSubmit, onCancel, onReload }: JournalFormProps) {
  const id = useId();
  const { values, set } = useFormValues({ entry_date: todayIso(), narration: "", reference: "" });
  const [lines, setLines] = useState<LineDraft[]>([EMPTY_LINE, EMPTY_LINE]);
  const { error, errors, saving, run } = useSubmitState();
  const debits = sum(lines, "debit");
  const credits = sum(lines, "credit");
  const difference = Math.round((debits - credits) * 100) / 100;
  const complete = lines.every(
    (line) => line.account_id && (Number(line.debit) > 0) !== (Number(line.credit) > 0),
  );
  const ready = complete && difference === 0 && debits > 0 && values.narration.trim() !== "";
  const options = accounts.map((a) => ({ value: a.id, label: `${a.code} ${a.name}` }));

  function update(index: number, patch: Partial<LineDraft>): void {
    setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        entry_date: values.entry_date,
        narration: values.narration.trim(),
        reference: values.reference.trim(),
        lines: lines.map(toInput),
      }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-3">
        <TextField
          id={`${id}-date`}
          label="Date"
          type="date"
          value={values.entry_date}
          onChange={(v) => set("entry_date", v)}
          error={errors.entry_date}
        />
        <TextField
          id={`${id}-narration`}
          label="Narration"
          value={values.narration}
          onChange={(v) => set("narration", v)}
          error={errors.narration}
          className="md:col-span-2"
        />
        <TextField
          id={`${id}-reference`}
          label="Reference"
          value={values.reference}
          onChange={(v) => set("reference", v)}
          error={errors.reference}
          hint="A voucher or document number, if any"
        />
      </div>
      <div className="flex flex-col gap-3">
        {lines.map((line, index) => (
          <div
            key={index}
            className="grid gap-3 rounded-lg border bg-card p-3 md:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] md:items-end"
          >
            <SelectField
              id={`${id}-account-${index}`}
              label="Account"
              value={line.account_id}
              onChange={(v) => update(index, { account_id: v })}
              options={options}
              placeholder="Choose an account"
            />
            <TextField
              id={`${id}-debit-${index}`}
              label="Debit INR"
              inputMode="decimal"
              value={line.debit}
              onChange={(v) => update(index, { debit: v })}
            />
            <TextField
              id={`${id}-credit-${index}`}
              label="Credit INR"
              inputMode="decimal"
              value={line.credit}
              onChange={(v) => update(index, { credit: v })}
            />
            <TextField
              id={`${id}-party-${index}`}
              label="Party"
              value={line.party_label}
              onChange={(v) => update(index, { party_label: v })}
            />
            <TextField
              id={`${id}-memo-${index}`}
              label="Memo"
              value={line.memo}
              onChange={(v) => update(index, { memo: v })}
            />
            {lines.length > MIN_LINES ? (
              <Button
                variant="ghost"
                size="sm"
                aria-label="Remove line"
                onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            ) : (
              <span />
            )}
          </div>
        ))}
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLines((current) => [...current, EMPTY_LINE])}
          >
            <Plus className="h-4 w-4" aria-hidden />
            Add line
          </Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground" aria-live="polite">
        Debits {formatInr(String(debits))} · Credits {formatInr(String(credits))} · Difference{" "}
        {formatInr(String(Math.abs(difference)))}
      </p>
      <FormActions saving={saving} submitLabel="Post entry" onCancel={onCancel} disabled={!ready} />
    </form>
  );
}
